import {MatchmakingMatchTypes, MatchType, ModeSettingEnum} from '@colonist/game-base-types'
import {activeMatchmakingMatchTypes} from '../../../shared/matchmaking/MatchmakingEnums'
import {uiManager} from './EntryHomepage'
import {SocketLobbySend} from '../../shared/scripts/socket/SocketLobbySend'
import {Scene} from './ui_html/UISceneTypes'
import {UIUrlController, UIUrlParameterNames} from '../../shared/scripts/ui_html/UIUrlController'
import {UIEntryUrlParameterController} from '../../shared/scripts/UIEntryUrlParameterController'
import {UIUtilsEjsVariables} from '../../shared/scripts/UIUtilsEjsVariables'
import {clientIntl} from '../../shared/scripts/ClientI18n'
import {StorageWrapper} from '../../shared/scripts/StorageWrapper'
import {ViewPopupRewardsProgram} from './ui_popup/ViewPopupRewardsProgram'
import {SDKRequestHandler} from './discord/content_frame/sdk_requests/SDKRequestHandler.ts'
import {ShareActivityLinkAnalytics} from '../../../shared/entity_datas/EntityDatas'
import {UIWAdController} from './ui_ad/UIWAdController.ts'
import {ClientShuffleQueueController} from '../../shared/scripts/shuffle/ClientShuffleQueueController'
import {ReferralProgramUtils} from '../../shared/scripts/referral/ReferralProgramUtils.ts'

export class EntryHomepageUrlParameterController extends UIEntryUrlParameterController {
    static async loadParameterEvents() {
        const urlParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        for(const parameterName of urlParams.keys()) {
            const parameterValue = urlParams.get(parameterName)
            if(parameterValue == null) continue
            switch(parameterName as UIUrlParameterNames) {
                case UIUrlParameterNames.Campaign: this.loadCampaignMode(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.EnterMatchmaking: void this.loadMatchmakingQueue(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.EnterBotGame: void this.loadEnterBotGame(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.VideoAdTesting: return // intentionally returning to prevent ad test from being cleared from window
                case UIUrlParameterNames.Lobby: this.loadLobbyScene(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.Spectate: this.loadSpectateScene(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.MainPage: this.loadMainPageScene(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.SelectMode: this.loadModeSelection(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.SuppressPopupsOnLoad: this.handleSuppressPopups(parameterValue); this.removeParameter(); break
                case UIUrlParameterNames.DiscordLeaderboard: this.loadDiscordScene(parameterValue, Scene.DiscordLeaderboard); break
                case UIUrlParameterNames.DiscordStore: this.loadDiscordScene(parameterValue, Scene.DiscordStore); break
                case UIUrlParameterNames.DiscordAbout: this.loadDiscordScene(parameterValue, Scene.DiscordAbout); break
                case UIUrlParameterNames.DiscordCareers: this.loadDiscordScene(parameterValue, Scene.DiscordCareers); break
                case UIUrlParameterNames.DiscordChampionship25: this.loadDiscordScene(parameterValue, Scene.DiscordChampionship25); break
                case UIUrlParameterNames.DiscordChampionship26: this.loadDiscordScene(parameterValue, Scene.DiscordChampionship26); break
                case UIUrlParameterNames.DiscordCommunity: this.loadDiscordScene(parameterValue, Scene.DiscordCommunity); break
                case UIUrlParameterNames.DiscordFAQ: this.loadDiscordScene(parameterValue, Scene.DiscordFAQ); break
                case UIUrlParameterNames.DiscordPrivacyPolicy: this.loadDiscordScene(parameterValue, Scene.DiscordPrivacyPolicy); break
                case UIUrlParameterNames.DiscordPatchNotes: this.loadDiscordScene(parameterValue, Scene.DiscordPatchNotes); break
                case UIUrlParameterNames.DiscordProfile: this.loadDiscordScene(parameterValue, Scene.DiscordProfile); break
                case UIUrlParameterNames.DiscordGifting: this.loadDiscordGiftScene(parameterValue); break
                case UIUrlParameterNames.DiscordValues: this.loadDiscordScene(parameterValue, Scene.DiscordValues); break
                case UIUrlParameterNames.DiscordValuesExamples: this.loadDiscordScene(parameterValue, Scene.DiscordValuesExamples); break
                case UIUrlParameterNames.DiscordTerms: this.loadDiscordScene(parameterValue, Scene.DiscordTerms); break
                case UIUrlParameterNames.DiscordPressKit: this.loadDiscordScene(parameterValue, Scene.DiscordPressKit); break
                case UIUrlParameterNames.DiscordRefundPolicy: this.loadDiscordScene(parameterValue, Scene.DiscordRefundPolicy); break
                case UIUrlParameterNames.DiscordShareActivity: this.loadShareActivityPopup(); this.removeParameter(); break
                case UIUrlParameterNames.ReferralInvitation: void ReferralProgramUtils.processReferralInvitation(parameterValue); this.removeParameter(); break
                default: break
            }
        }

        const roomOrGameId = UIUrlController.getRoomIdFromUrl()
        if(roomOrGameId != undefined) {
            await UIWAdController.initiatePopupActionFromUserState(uiManager?.adController.adblockState.isAdblockActive ?? false)
            await SocketLobbySend.accessGameFromLink(roomOrGameId)
        }
    }

    static removeParameter() {
        window.history.replaceState({}, document.title, clientIntl.getLanguagePathPrefix() || '/')
    }

    static loadLobbyScene(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.Lobby)
    }

    static loadSpectateScene(parameter: string) {
        this.loadLobbyScene(parameter)
        uiManager.lobby.tableController.headerController.openSpectateTab()
    }

    static loadMainPageScene(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.MainPage)
    }

    static loadCampaignMode(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        void SocketLobbySend.continueCampaignMode()
    }

    static async loadMatchmakingQueue(parameter: string) {
        const requestedMatchType = Number(parameter) as MatchmakingMatchTypes
        if(!activeMatchmakingMatchTypes.has(requestedMatchType)) return

        await uiManager.modeSelector.waitUntilInitialized

        if(requestedMatchType == MatchmakingMatchTypes.UnrankedShuffle) await ClientShuffleQueueController.waitForShuffleQueueData()

        uiManager.sceneController.loadScene(Scene.MainPage)
        uiManager.modeSelector.selectCardByMatchType(requestedMatchType)
        uiManager.matchmakingController.sendFindGameMessageToServer(requestedMatchType)
    }

    static async loadEnterBotGame(parameterValue: string) {
        const modeSetting = Number(parameterValue) as ModeSettingEnum
        if(modeSetting === ModeSettingEnum.ColonistRush4P) {
            void SocketLobbySend.clickedStartColonistRushBotGame()
            return
        }
        void SocketLobbySend.clickedStartHardBotGame()
    }

    static loadModeSelection(parameter: string) {
        const requestedMatchType = parameter as MatchType

        void uiManager.goToMainPageAndSelectMatchType(requestedMatchType)
    }

    static loadDiscordScene(parameter: string, scene: Scene) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(scene)
        history.replaceState(null, '', window.location.pathname)
    }

    static loadShareActivityPopup() {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return

        new ViewPopupRewardsProgram(ShareActivityLinkAnalytics.ReturnToHomeAfterGameEndReferralProgramPopupTriggered, ShareActivityLinkAnalytics.ReturnToHomeAfterGameEndReferralProgramSuccessfullyShared)

        SDKRequestHandler.sendShareActivityLinkAnalytic(ShareActivityLinkAnalytics.ReturnToHomeAfterGameEndReferralProgramPrePopupTriggered)

        StorageWrapper.setLastShareActivityLinkPopupTime(new Date())
    }

    private static loadDiscordGiftScene(parameter: string) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return
        if(parameter == this.DEFAULT_PARAMETER_VALUE) return

        uiManager.sceneController.loadScene(Scene.DiscordGifting, {recipientUsername: parameter})
    }

    static handleSuppressPopups(parameter: string) {
        if(parameter != this.DEFAULT_PARAMETER_VALUE) return

        UIEntryUrlParameterController.suppressPopupsOnLoad = true
    }
}
